/**
 * The app shell's view of the staff chat (SR-14): one call in App.tsx.
 *
 * With `?x=staff-chat` off, it answers `enabled: false` and `isOldMessagesAddress: false`, and starts nothing: no
 * staff-chat request and no hub listener. On, it listens for the staff-chat hub events (one connection of its own;
 * the Team dock and the dashboard card read the caches it keeps current).
 *
 * Sign-out or a change of user (with the switch on or off, and across the shell unmounting for /login): the dock
 * goes back to its first state and the staff-chat caches (threads, messages, unread, directory, live read markers)
 * are reset, so the next user never sees the last user's conversations or unread total.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/auth/auth-store';
import { useStaffChatEnabled } from '@/lib/use-staff-chat-enabled';
import { resetStaffChatDock, useStaffChatDock } from './dock/dock-store';
import { useStaffChatRealtime } from './realtime/use-staff-chat-realtime';
import { STAFF_CHAT_READS_ROOT, staffChatKeys } from './services/staff-chat-cache';
import { isMessagesPath } from './staff-chat-paths';

export interface StaffChatShell {
  enabled: boolean;
  /**
   * The retired `/messages` page's address with the switch on: the shell sends it to the Dashboard (keeping
   * `?thread=<id>`, which opens the dock on that conversation), so old links still land somewhere useful.
   */
  isOldMessagesAddress: boolean;
}

export function useStaffChatShell(pathname: string): StaffChatShell {
  const enabled = useStaffChatEnabled();
  useStaffChatRealtime(enabled);
  useForgetOnUserChange();
  return { enabled, isOldMessagesAddress: enabled && isMessagesPath(pathname) };
}

function useForgetOnUserChange(): void {
  const userId = useAuthStore((s) => s.user?.userId ?? null);
  const qc = useQueryClient();
  // Synchronises the dock store and the query cache (both outside React, both outliving the shell, which unmounts on
  // the way to /login) with who is signed in. The owner lives in the store, so a remount for the next user still sees
  // the previous one.
  //   - signed out (no user): removeQueries, so nothing still on screen asks again without a sign-in token;
  //   - a new user: resetQueries, so anything still on screen drops the old data at once and reads again for them.
  useEffect(() => {
    const { ownerUserId } = useStaffChatDock.getState();
    if (ownerUserId === userId) return;
    resetStaffChatDock();
    useStaffChatDock.setState({ ownerUserId: userId });
    if (ownerUserId === null) return;
    if (userId === null) {
      qc.removeQueries({ queryKey: staffChatKeys.all });
      qc.removeQueries({ queryKey: [STAFF_CHAT_READS_ROOT] });
    } else {
      void qc.resetQueries({ queryKey: staffChatKeys.all });
      void qc.resetQueries({ queryKey: [STAFF_CHAT_READS_ROOT] });
    }
  }, [userId, qc]);
}
